import axios from "axios";
import { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import { ADMIN_API_URL, isAffiliateTestEnvironment } from "../../utils/env";

const MAX_REQUEST_BYTES = 256 * 1024;
const digest = (value: unknown): value is string =>
  typeof value === "string" && /^[a-f0-9]{64}$/.test(value);

interface RecoveryBoundary {
  sequence: number;
  offset: number;
  timestamp: string;
}

interface RecoveryProvenance {
  request_id: string;
  actor: string;
  fingerprint: string;
  source_snapshot_sha256: string;
  source: RecoveryBoundary;
  replay_boundary: RecoveryBoundary;
  replay_proof_sha256: string;
  prior_bundle_sha256: string;
  imported_bundle_sha256: string;
  applied_sequence: number;
  applied_at: string;
}

interface RecoveryStatus {
  current_bundle_sha256: string;
  pristine: boolean;
  provenance?: RecoveryProvenance;
}

interface RequestFile {
  file: File;
  requestID: string;
  expectedCurrent: string;
  importedBundle: string;
  sourceSnapshot: string;
  replayProof: string;
}

function readFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Cannot read the selected file."));
    reader.readAsText(file);
  });
}

async function inspectFile(file: File): Promise<RequestFile> {
  if (!file.size || file.size > MAX_REQUEST_BYTES)
    throw new Error("Choose a nonempty JSON request no larger than 256 KiB.");
  let parsed;
  try {
    // Metadata inspection only. The original immutable File is the POST body;
    // no parsed number, object, whitespace or encoding is sent back to Axios.
    parsed = JSON.parse(await readFile(file));
  } catch {
    throw new Error("Choose a valid recovery-request.json file.");
  }
  const payload = parsed?.payload;
  if (
    typeof parsed?.request_id !== "string" ||
    !parsed.request_id.trim() ||
    parsed.request_id.length > 128 ||
    !digest(payload?.expected_current_sha256) ||
    !digest(payload?.imported_bundle_sha256) ||
    !digest(payload?.source_snapshot_sha256) ||
    !digest(payload?.replay_proof_sha256) ||
    !payload?.bundle ||
    typeof payload.bundle !== "object" ||
    Array.isArray(payload.bundle)
  )
    throw new Error(
      "The file is missing recovery identity or evidence hashes."
    );
  return {
    file,
    requestID: parsed.request_id,
    expectedCurrent: payload.expected_current_sha256,
    importedBundle: payload.imported_bundle_sha256,
    sourceSnapshot: payload.source_snapshot_sha256,
    replayProof: payload.replay_proof_sha256,
  };
}

function validStatus(value: RecoveryStatus): boolean {
  return (
    digest(value?.current_bundle_sha256) && typeof value?.pristine === "boolean"
  );
}

function receiptBoundary(value: RecoveryBoundary) {
  if (
    !Number.isSafeInteger(value?.sequence) ||
    value.sequence <= 0 ||
    !Number.isSafeInteger(value?.offset) ||
    value.offset <= 0 ||
    typeof value?.timestamp !== "string" ||
    !value.timestamp
  )
    return undefined;
  return {
    sequence: value.sequence,
    offset: value.offset,
    timestamp: value.timestamp,
  };
}

function recoveryReceipt(status: RecoveryStatus | undefined) {
  const p = status?.provenance;
  if (!status || status.pristine || !p) return undefined;
  const source = receiptBoundary(p.source);
  const replayBoundary = receiptBoundary(p.replay_boundary);
  if (
    !source ||
    !replayBoundary ||
    typeof p.request_id !== "string" ||
    !p.request_id ||
    typeof p.actor !== "string" ||
    !p.actor ||
    !Number.isSafeInteger(p.applied_sequence) ||
    p.applied_sequence <= 0 ||
    typeof p.applied_at !== "string" ||
    !p.applied_at ||
    ![
      p.fingerprint,
      p.source_snapshot_sha256,
      p.replay_proof_sha256,
      p.prior_bundle_sha256,
      p.imported_bundle_sha256,
    ].every(digest)
  )
    return undefined;
  // Export only the GET status contract. Never include uploaded state, auth
  // configuration, or unexpected response properties in the receipt file.
  return {
    current_bundle_sha256: status.current_bundle_sha256,
    pristine: status.pristine,
    provenance: {
      request_id: p.request_id,
      actor: p.actor,
      fingerprint: p.fingerprint,
      source_snapshot_sha256: p.source_snapshot_sha256,
      source,
      replay_boundary: replayBoundary,
      replay_proof_sha256: p.replay_proof_sha256,
      prior_bundle_sha256: p.prior_bundle_sha256,
      imported_bundle_sha256: p.imported_bundle_sha256,
      applied_sequence: p.applied_sequence,
      applied_at: p.applied_at,
    },
  };
}

function recorded(
  status: RecoveryStatus | undefined,
  request: RequestFile | undefined
) {
  return Boolean(
    request &&
      status?.pristine === false &&
      status.provenance?.request_id === request.requestID &&
      status.provenance?.imported_bundle_sha256 === request.importedBundle &&
      status.current_bundle_sha256 === request.importedBundle
  );
}

function failureMessage(error: unknown): string {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 401 || status === 403)
    return "Sign in with the required recovery permissions (READ_SYSTEM / WRITE_SYSTEM).";
  if (status && status >= 400 && status < 500)
    return "The backend rejected this request. Refresh status and check the audited recovery file.";
  // Do not render arbitrary response/error text, which could include the raw
  // request, Axios authentication configuration, or snapshot values.
  return "The outcome is unconfirmed. Check recovery status before retrying the same request.";
}

export function RecoveryPanel({
  verifiedTestnet,
}: {
  verifiedTestnet: boolean;
}) {
  const { tokenId, queryConfig, isAuthenticating } = useContext(AuthContext);
  const allowed = isAffiliateTestEnvironment();
  const endpoint = ADMIN_API_URL("affiliates/recovery");
  const authorized =
    allowed && verifiedTestnet && Boolean(tokenId) && !isAuthenticating;
  // This identity stays in memory and is never rendered, logged or persisted.
  const identity = `${process.env.REACT_APP_ENVIRONMENT}:${endpoint}:${
    tokenId || ""
  }`;
  const currentIdentity = useRef(identity);
  currentIdentity.current = identity;
  const currentAuthorization = useRef(authorized);
  currentAuthorization.current = authorized;
  const [status, setStatus] = useState<RecoveryStatus>();
  const [statusIdentity, setStatusIdentity] = useState<string>();
  const [request, setRequest] = useState<RequestFile>();
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const busyRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setStatus(undefined);
    setStatusIdentity(undefined);
    setRequest(undefined);
    setAttempted(false);
    setError(undefined);
    setNotice(undefined);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [identity]);

  const freshStatus = statusIdentity === identity ? status : undefined;
  const receipt = recoveryReceipt(freshStatus);
  const applied = recorded(freshStatus, request);
  const ready = authorized && freshStatus?.pristine === true;
  const canSubmit =
    ready && request?.expectedCurrent === freshStatus?.current_bundle_sha256;

  const getStatus = async () => {
    const response = await axios.get<RecoveryStatus>(endpoint, queryConfig());
    if (!validStatus(response.data)) throw new Error("Invalid recovery status");
    if (currentIdentity.current !== identity || !currentAuthorization.current)
      throw new Error("Authorization changed");
    setStatus(response.data);
    setStatusIdentity(identity);
    return response.data;
  };

  const check = async () => {
    if (!authorized || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(undefined);
    try {
      await getStatus();
    } catch (failure) {
      if (currentIdentity.current === identity) {
        setStatus(undefined);
        setStatusIdentity(undefined);
        setError(failureMessage(failure));
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const choose = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!authorized || !ready || busyRef.current || attempted || !file) return;
    setRequest(undefined);
    setError(undefined);
    setNotice(undefined);
    busyRef.current = true;
    setBusy(true);
    try {
      const selected = await inspectFile(file);
      if (currentIdentity.current === identity) setRequest(selected);
    } catch (failure) {
      if (currentIdentity.current === identity)
        setError((failure as Error).message);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!authorized || !canSubmit || !request || busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(undefined);
    try {
      const latest = await getStatus();
      if (
        !latest.pristine ||
        latest.current_bundle_sha256 !== request.expectedCurrent
      ) {
        setNotice(
          "State changed. Review the current recovery status before continuing."
        );
        return;
      }
      setAttempted(true);
      const config = queryConfig();
      const response = await axios.post(endpoint, request.file, {
        ...config,
        headers: { ...config.headers, "Content-Type": "application/json" },
        // Preserve every original byte, including integers above 2^53. Do not
        // allow Axios's JSON transform to parse/stringify this audited body.
        transformRequest: [(body) => body],
      });
      if (currentIdentity.current !== identity) return;
      if (
        response.data?.success !== true ||
        response.data?.request_id !== request.requestID
      )
        throw new Error("Unconfirmed command receipt");
      setNotice(
        "Request accepted. The recovery status must confirm that it was applied."
      );
      await getStatus();
    } catch (failure) {
      if (currentIdentity.current === identity) {
        setStatus(undefined);
        setStatusIdentity(undefined);
        setError(failureMessage(failure));
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const downloadReceipt = () => {
    if (!authorized || !receipt || busyRef.current) return;
    const blob = new Blob([`${JSON.stringify(receipt, null, 2)}\n`], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "affiliate-recovery-receipt.json";
    document.body.appendChild(link);
    try {
      link.click();
    } finally {
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  };

  if (!allowed) return null;
  return (
    <section aria-label="Affiliate state recovery">
      <details>
        <summary>Recover verified testnet affiliate state</summary>
        <p>
          Upload the audited recovery request. The backend checks authorization,
          evidence and unchanged target state. Import keeps the pilot and weekly
          cash disabled; it does not certify the live tail.
        </p>
        {!authorized && (
          <p>Admin sign-in and a verified testnet backend are required.</p>
        )}
        <button type="button" disabled={!authorized || busy} onClick={check}>
          Check recovery status
        </button>
        <button
          type="button"
          disabled={!authorized || !receipt || busy}
          onClick={downloadReceipt}
        >
          Download recovery receipt
        </button>
        {freshStatus && (
          <div>
            <p>
              Target: {freshStatus.pristine ? "Pristine" : "Already populated"}
            </p>
            <p>
              Current bundle: <code>{freshStatus.current_bundle_sha256}</code>
            </p>
          </div>
        )}
        <label htmlFor="affiliate-recovery-request-file">
          Audited recovery-request.json (maximum 256 KiB)
          <input
            id="affiliate-recovery-request-file"
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            disabled={!ready || busy || attempted}
            onChange={choose}
          />
        </label>
        {request && (
          <dl>
            <dt>Request ID</dt>
            <dd>
              <code>{request.requestID}</code>
            </dd>
            <dt>Request size</dt>
            <dd>{request.file.size} bytes</dd>
            <dt>Source snapshot</dt>
            <dd>
              <code>{request.sourceSnapshot}</code>
            </dd>
            <dt>Imported bundle</dt>
            <dd>
              <code>{request.importedBundle}</code>
            </dd>
            <dt>Replay proof</dt>
            <dd>
              <code>{request.replayProof}</code>
            </dd>
            <dt>Expected current bundle</dt>
            <dd>
              <code>{request.expectedCurrent}</code>
            </dd>
          </dl>
        )}
        {ready && request && !canSubmit && (
          <p>The request does not match the current target digest.</p>
        )}
        {error && <div role="alert">{error}</div>}
        {notice && !applied && <div role="status">{notice}</div>}
        {applied && (
          <div role="status">
            Recovery recorded for this request and bundle. Keep the pilot
            disabled until the tail and downstream audit are certified.
          </div>
        )}
        <button
          type="button"
          disabled={!canSubmit || busy || applied}
          onClick={submit}
        >
          {attempted
            ? "Retry same recovery request"
            : "Submit verified recovery"}
        </button>
      </details>
    </section>
  );
}
